import type { EvalTaskSpec } from '@sbc/domain-models/agents';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { ApiError } from '@/lib/api/client';
import { useEvalSuite, useUpdateTask } from '@/lib/api/eval-suites';

import { TaskEditor } from './task-editor';
import { taskSpecToYaml } from './task-yaml';

export function TaskEditPage({ suiteId, taskId }: { suiteId: string; taskId: string }) {
  const navigate = useNavigate();
  const suiteQuery = useEvalSuite(suiteId);
  const updateTask = useUpdateTask(suiteId);
  const [error, setError] = useState<string | null>(null);

  const back = () => navigate({ to: '/evals/suites/$suiteId', params: { suiteId } });

  if (suiteQuery.isPending) {
    return <p className="text-sm">Loading…</p>;
  }
  if (suiteQuery.isError) {
    return (
      <p className="text-sm text-[var(--color-destructive)]">
        Failed to load suite: {suiteQuery.error.message}
      </p>
    );
  }

  const task = suiteQuery.data.tasks.find((candidate) => candidate.id === taskId);
  if (!task) {
    return <p className="text-sm">Task not found.</p>;
  }

  const initialText = taskSpecToYaml({
    taskKey: task.taskKey,
    suite: task.suite,
    input: task.input,
    grade: task.grade,
    trials: task.trials,
    passThreshold: task.passThreshold,
    ...(task.expectTools ? { expectTools: task.expectTools } : {}),
    ...(task.expectNoTools ? { expectNoTools: task.expectNoTools } : {}),
    ...(task.expectAnswer ? { expectAnswer: task.expectAnswer } : {}),
  });

  const onSubmit = (spec: EvalTaskSpec) => {
    setError(null);
    updateTask.mutate(
      { taskId, input: spec },
      {
        onSuccess: back,
        onError: (caught) =>
          setError(caught instanceof ApiError ? caught.message : 'Could not save the task.'),
      },
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Edit task</h1>
      <TaskEditor
        initialText={initialText}
        submitting={updateTask.isPending}
        submitLabel="Save changes"
        error={error}
        onSubmit={onSubmit}
        onCancel={back}
      />
    </div>
  );
}
